import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import FinancialGoalsCard from './GoalBasedInvestment/FinancialGoalsCard';
import PersonalizedStrategyCard from './GoalBasedInvestment/PersonalizedStrategyCard';
import GoalsProgressCard from './GoalBasedInvestment/GoalsProgressCard';

// ImageCard component
const ImageCard = () => {
  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 h-full min-h-[22rem] sm:min-h-[26rem] flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <img
          src="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/image.png"
          alt="Track progress"
          className="w-full h-full object-cover rounded-xl"
        />
      </div>
    </div>
  );
};

const GoalBasedInvestment = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Touch/swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwipingActive, setIsSwipingActive] = useState(false);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const slides = [
    {
      title: "Set financial goals that truly matter",
      description: "Prioritize investments based on your life goals like buying a house, child education, or retirement.",
      stats: {
        portfolio: "+18.45%",
        benchmark: "+15.20%"
      }
    },
    {
      title: "Get personalized investment strategy",
      description: "Our algorithms craft a goal-based strategy tailored to your timeline and risk tolerance.",
      stats: {
        portfolio: "+20.12%",
        benchmark: "+16.50%"
      }
    },
    {
      title: "Track progress & stay on course",
      description: "Easily monitor your journey and make timely adjustments with expert assistance.",
      stats: {
        portfolio: "+17.78%",
        benchmark: "+16.10%"
      }
    }
  ];

  // Array of left side card components
  const leftSideCards = [
    <FinancialGoalsCard key="financial-goals" />,
    <PersonalizedStrategyCard key="personalized-strategy" />,
    <GoalsProgressCard key="goals-progress" />,
  ];

  // Touch event handlers
  const onTouchStart = (e) => {
    setTouchEnd(null); // Otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwipingActive(true);
    setIsPaused(true); // Pause auto-advance while user is touching
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwipingActive(false);
      setIsPaused(false);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - go to next slide
      const nextSlide = (activeSlide + 1) % slides.length;
      handleSlideChange(nextSlide);
    } else if (isRightSwipe) {
      // Swipe right - go to previous slide
      const prevSlide = activeSlide === 0 ? slides.length - 1 : activeSlide - 1;
      handleSlideChange(prevSlide);
    } else {
      // Not a valid swipe, resume auto-advance
      setIsPaused(false);
    }
    
    setIsSwipingActive(false);
  };

  // Function to start the auto-advance timer
  const startAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      if (!isPaused && !isSwipingActive) {
        setActiveSlide((prev) => (prev + 1) % slides.length);
      }
    }, 5000);
  }, [isPaused, isSwipingActive, slides.length]);

  // Function to handle manual slide changes
  const handleSlideChange = useCallback((newIndex) => {
    setActiveSlide(newIndex);
    
    // Clear existing timers
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Pause briefly to let user see their selection, then resume
    setIsPaused(true);
    timeoutRef.current = setTimeout(() => {
      setIsPaused(false);
      startAutoAdvance();
    }, 2000); // 2 second pause after user interaction
  }, [startAutoAdvance]);

  // Handle mouse enter/leave for pause on hover (desktop only)
  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      setIsPaused(true);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setIsPaused(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize auto-advance
  useEffect(() => {
    startAutoAdvance();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [startAutoAdvance]);

  return (
    <div className="w-full bg-white">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-[#113262] text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-center mb-8 lg:mb-12">
          Start a Goal Based Investment
        </h2>

        {isMobile ? (
          <div className="space-y-6">
            {/* Swipeable card container for mobile */}
            <div 
              className="relative overflow-hidden"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ 
                  transform: `translateX(-${activeSlide * 100}%)`,
                  touchAction: 'pan-y pinch-zoom' // Allow vertical scrolling but capture horizontal swipes
                }}
              >
                {leftSideCards.map((card, index) => (
                  <div 
                    key={index}
                    className="w-full flex-shrink-0"
                  >
                    {card}
                  </div>
                ))}
              </div>
              
              {/* Optional: Visual feedback during swipe */}
              {isSwipingActive && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="h-full w-full bg-black bg-opacity-5 rounded-xl"></div>
                </div>
              )}
            </div>

            {/* Navigation dots */}
            <div className="flex justify-center gap-2">
              {leftSideCards.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSlideChange(idx)}
                  className={`h-2.5 w-2.5 rounded-full transition-all duration-300 hover:scale-110 ${
                    activeSlide === idx ? 'bg-[#1C52A0]' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Swipe instruction text */}
            <div className="text-center">
              <a
              
                href="https://club.maxiomwealth.com/goals/finplan/create"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] text-white rounded-lg hover:scale-105 hover:shadow-xl shadow-lg transition-all duration-300 group"
                ><span className="font-semibold">Plan my investments</span>
                <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>
          </div>
        ) : (
          // Desktop View (unchanged)
          <div 
            className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* LEFT SIDE - Your Custom Cards */}
            <div className="hidden lg:block">
              <div className="transition-all duration-500 ease-in-out">
                {leftSideCards[activeSlide]}
              </div>
            </div>

            {/* Mobile version of your cards */}
            <div className="lg:hidden w-full">
              <div className="transition-all duration-500 ease-in-out">
                {leftSideCards[activeSlide]}
              </div>
            </div>

            {/* Right Content - Original Slides Content */}
            <div className="space-y-6 lg:space-y-8 w-full">
              <div className="space-y-6 relative">
                {/* Progress Indicators Container */}
                <div className="absolute right-0 h-full flex flex-col">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className={`w-1 flex-1 transition-all duration-500 rounded-none ${
                        index === activeSlide ? 'bg-[#1C52A0]' : 'bg-[#E8EEF6]'
                      }`}
                    />
                  ))}
                </div>

                {slides.map((slide, index) => (
                  <div
                    key={index}
                    onClick={() => handleSlideChange(index)}
                    className={`pr-6 transition-all duration-500 ease-in-out cursor-pointer hover:opacity-90 text-left transform ${
                      index === activeSlide
                        ? 'opacity-100 translate-y-0 scale-[1.02]'
                        : 'opacity-60 translate-y-2 hover:opacity-75'
                    }`}
                  >
                    <h3 className={`text-base sm:text-lg font-medium transition-colors duration-300 ${
                      index === activeSlide ? 'text-[#113262]' : 'text-[#436FB0]'
                    }`}>
                      {slide.title}
                    </h3>
                    <p className={`mt-2 text-sm sm:text-base transition-colors duration-300 ${
                      index === activeSlide ? 'text-[#262626]' : 'text-[#545454]'
                    }`}>
                      {slide.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <a 
                  href='https://club.maxiomwealth.com/goals/finplan/create' 
                  className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] text-white rounded-lg hover:scale-105 hover:shadow-xl shadow-lg transition-all duration-300 group"
                >
                  <span>Start Goal Based Investment</span>
                  <ArrowRight className="ml-2 sm:ml-3 h-4 sm:h-5 w-4 sm:w-5 transform group-hover:translate-x-1 transition-transform duration-200" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalBasedInvestment;